import { faBadgePercent, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { INVALID_PROMOCODE_MESSAGE, PROMO_CODE_MAX_LENGTH } from 'utils/promoCodeUtils';
import {
    clearPromocodeError,
    submitPromocodeThunk,
    removeSubmittedPromocodeThunk
} from 'actions/promocodes/OnlineBookingPromocodeActions';
import { Button } from 'components/UI/Button/Button';
import clsx from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    getAppliedPromocodeName,
    getPromocodeError,
    getPromocodeLoading
} from 'reducers/promocodes/PromocodesSelectors';
import { dispatch } from 'store';
import classes from './PromoCodeField.module.scss';

export const PromoCodeField: React.FC = () => {
    const appliedName = useSelector(getAppliedPromocodeName);
    const storeError = useSelector(getPromocodeError);
    const loading = useSelector(getPromocodeLoading);
    const [code, setCode] = React.useState('');
    const [localError, setLocalError] = React.useState<string>();

    const applied = !!appliedName;
    const error = localError || (storeError ? INVALID_PROMOCODE_MESSAGE : undefined);

    const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCode(event.target.value.slice(0, PROMO_CODE_MAX_LENGTH));
        setLocalError(undefined);

        if (storeError) {
            dispatch(clearPromocodeError());
        }
    };

    const handleApply = async () => {
        if (!code.trim() || loading) {
            return;
        }

        try {
            await dispatch(submitPromocodeThunk(code));
            setLocalError(undefined);
        } catch {
            setLocalError(INVALID_PROMOCODE_MESSAGE);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleApply();
        }
    };

    const handleRemove = async () => {
        try {
            await dispatch(removeSubmittedPromocodeThunk());
            setCode('');
        } catch {
            // Field error comes from promocodes state.
        }
    };

    return (
        <div className={classes.root}>
            <span className={classes.label}>Promo Code</span>

            {!applied && (
                <>
                    <div className={clsx(classes.inputRow, error && classes.inputError)}>
                        <input
                            className={classes.input}
                            value={code}
                            placeholder="Enter your promo code"
                            autoComplete="off"
                            spellCheck={false}
                            maxLength={PROMO_CODE_MAX_LENGTH}
                            disabled={loading}
                            onChange={handleCodeChange}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            variant="black"
                            size="small"
                            className={classes.applyButton}
                            loading={loading}
                            disabled={!code.trim() || loading}
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                    </div>

                    {error && <p className={classes.error}>{error}</p>}
                </>
            )}

            {applied && (
                <button
                    type="button"
                    className={classes.chip}
                    aria-label={`Remove promo code ${appliedName}`}
                    disabled={loading}
                    onClick={handleRemove}
                >
                    <FontAwesomeIcon icon={faBadgePercent} className={classes.chipIcon} />
                    <span>{appliedName}</span>
                    <FontAwesomeIcon icon={faTimes} className={classes.chipIcon} />
                </button>
            )}
        </div>
    );
};
